/**
 * Unified CPSC transform utility.
 *
 * Composes existing utilities to provide a single API for transforming
 * CPSC data between form state and GraphQL schema format.
 *
 * - toSchema: Cleans fields + transforms entities (for form submission)
 * - toForm: Transforms entities for form consumption (for display)
 */
import omit from 'lodash/omit';
import moment from 'moment';
import type {
  UsCpscPgaProgram,
  UsCpscPgaProgramInput,
  CpscItem,
} from '__generated__/graphql';
import {
  transformCpscEntities,
  type CpscEntityFormState,
} from './transformEntities';
import {
  transformProductIdentifiers,
  type ProductIdentifierFormState,
} from './transformProductIdentifiers';
import { cleanCpscFields } from './cleanCpscFields';

const normalizeManufactureDateToForm = (
  value?: string | null,
): moment.Moment | undefined => {
  if (!value) return undefined;
  const parsed = moment(value, ['MM-YYYY', 'MM/YYYY'], true);
  return parsed.isValid() ? parsed : undefined;
};

const normalizeManufactureDateToSchema = (
  value?: moment.Moment | string | null,
): string | undefined => {
  if (!value) return undefined;
  if (moment.isMoment(value)) return value.format('MM-YYYY');
  if (typeof value === 'string') return value.replace('/', '-');
  return undefined;
};

/**
 * Form state type for CpscItem with:
 * - flat entities[] array (instead of structured entity arrays)
 * - unified productIdentifiers[] (instead of itemIdentifiers + productCodes)
 * - manufactureDate as Moment object for DatePicker compatibility
 */
export type CpscItemFormState = Omit<
  CpscItem,
  | 'manufacturers'
  | 'laboratories'
  | 'certifyingEntities'
  | 'pointOfContacts'
  | 'itemIdentifiers'
  | 'productCodes'
  | 'manufactureDate'
> & {
  entities?: CpscEntityFormState[];
  productIdentifiers?: ProductIdentifierFormState[];
  manufactureDate?: moment.Moment | string;
};

/**
 * Form state type for CPSC program with flat entities[] per item
 */
export type UsCpscFormState = Omit<UsCpscPgaProgram, 'items'> & {
  items?: CpscItemFormState[];
};

// Keys to omit when transforming to schema format
const SCHEMA_OMIT_KEYS = ['entities', 'productIdentifiers'] as const;

// Keys to omit when transforming to form format
const FORM_OMIT_KEYS = [
  'manufacturers',
  'laboratories',
  'certifyingEntities',
  'pointOfContacts',
  'itemIdentifiers',
  'productCodes',
] as const;

/**
 * Unified CPSC transform utility for form ↔ schema transformations.
 *
 * @example
 * // Form submission (form → schema):
 * const schemaData = transformCpsc.toSchema(formData);
 *
 * @example
 * // Load data (schema → form):
 * const formData = transformCpsc.toForm(schemaData);
 */
export const transformCpsc = {
  /**
   * Transform form data to schema format for API submission.
   *
   * @param {Partial<UsCpscFormState>} formData - Form data to transform
   * @return {UsCpscPgaProgramInput} Cleaned and transformed schema data
   */
  toSchema: (formData: Partial<UsCpscFormState>): UsCpscPgaProgramInput => {
    // Step 1: Transform items (entities flat → structured, productIdentifiers → split arrays)
    const transformedItems = formData.items?.map((item) => ({
      ...omit(item, SCHEMA_OMIT_KEYS),
      ...transformCpscEntities.toSchema(item.entities ?? []),
      ...transformProductIdentifiers.toSchema(item.productIdentifiers),
      manufactureDate: normalizeManufactureDateToSchema(item.manufactureDate),
    })) as CpscItem[] | undefined;

    // Step 2: Clean fields by processing type
    return cleanCpscFields({
      ...formData,
      items: transformedItems,
    } as UsCpscPgaProgram);
  },

  /**
   * Transform schema data to form format for display.
   *
   * @param {UsCpscPgaProgram} schemaData - Schema data to transform
   * @return {Partial<UsCpscFormState>} Transformed form data
   */
  toForm: (schemaData: UsCpscPgaProgram): Partial<UsCpscFormState> => {
    // Transform items (entities structured → flat, split arrays → productIdentifiers)
    const transformedItems = schemaData.items?.map((item) => ({
      ...omit(item, FORM_OMIT_KEYS),
      entities: transformCpscEntities.toForm(item),
      productIdentifiers: transformProductIdentifiers.toForm(item),
      manufactureDate: normalizeManufactureDateToForm(item.manufactureDate),
    })) as CpscItemFormState[] | undefined;

    return {
      ...omit(schemaData, ['items']),
      items: transformedItems,
    };
  },
};

export default transformCpsc;
